/**
 * IDF Parser
 *
 * Converts a stream of tokens into a structured IDFDocument.
 * Handles error recovery to provide partial results on malformed input.
 */

import { tokenize } from './lexer';
import {
  type IDFDocument,
  type IDFField,
  type IDFObject,
  type ParseError,
  type ParseResult,
  type Token,
  TokenType,
} from './types';

/**
 * Generate a unique ID for IDF objects.
 */
function generateId(): string {
  return `obj_${String(Date.now())}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Parse units from an inline comment.
 * Units are enclosed in curly braces, e.g., "{deg}", "{W/m2-K}"
 */
function parseUnitsFromComment(comment: string): string | undefined {
  const match = comment.match(/\{([^}]+)\}/);
  return match ? match[1] : undefined;
}

/**
 * Create an IDFField, only including optional properties if defined.
 */
function createField(value: string, inlineComment?: string, units?: string): IDFField {
  const field: IDFField = { value };
  if (inlineComment !== undefined) field.inlineComment = inlineComment;
  if (units !== undefined) field.units = units;
  return field;
}

/**
 * Parser for IDF (Input Data File) format.
 */
export class IDFParser {
  private tokens: Token[] = [];
  private current: number = 0;
  private errors: ParseError[] = [];

  /**
   * Parse IDF text into a structured document.
   */
  parse(input: string): ParseResult {
    this.tokens = tokenize(input);
    this.current = 0;
    this.errors = [];

    const document = this.parseDocument();

    return {
      document,
      errors: this.errors,
      success: this.errors.filter((e) => e.severity === 'error').length === 0,
    };
  }

  private parseDocument(): IDFDocument {
    const headerComments: string[] = [];
    const objects: IDFObject[] = [];
    let version = '';

    // Skip initial whitespace
    this.skipWhitespaceAndNewlines();

    // Collect header comments (comments at the very start before the first object)
    while (this.check(TokenType.Comment)) {
      headerComments.push(this.advance().value);
      this.skipWhitespaceAndNewlines();
    }

    // Parse objects
    while (!this.isAtEnd()) {
      this.skipWhitespaceAndNewlines();

      if (this.isAtEnd()) break;

      if (this.check(TokenType.Comment) || this.check(TokenType.FieldValue)) {
        const obj = this.parseObject();
        if (obj) {
          objects.push(obj);

          // Extract version
          if (obj.className.toLowerCase() === 'version' && obj.fields.length > 0) {
            const firstField = obj.fields[0];
            if (firstField) {
              version = firstField.value;
            }
          }
        }
      } else {
        // Unexpected token, skip it
        const token = this.advance();
        this.addError(`Unexpected token: ${token.value}`, token.line, token.column);
      }
    }

    // Collect any trailing comments
    const trailingComments: string[] = [];
    // (Already at end, trailing comments would have been consumed)

    return {
      version,
      objects,
      headerComments,
      trailingComments,
    };
  }

  private parseObject(preCollectedComments: string[] = []): IDFObject | null {
    const leadingComments: string[] = [...preCollectedComments];

    // Collect leading comments
    while (this.check(TokenType.Comment)) {
      leadingComments.push(this.advance().value);
      this.skipWhitespaceAndNewlines();
    }

    // Expect class name
    if (!this.check(TokenType.FieldValue)) {
      if (!this.isAtEnd()) {
        const token = this.peek();
        this.addError(`Expected object class name`, token.line, token.column);
        this.synchronize();
      }
      return null;
    }

    const classNameToken = this.advance();
    const className = classNameToken.value;
    const startLine = classNameToken.line;

    // Skip whitespace to comma
    this.skipWhitespace();

    // Expect comma after class name
    if (!this.check(TokenType.Comma)) {
      // Could be an empty object with just semicolon (rare)
      if (this.check(TokenType.Semicolon)) {
        this.advance();
        return {
          id: generateId(),
          className,
          fields: [],
          leadingComments,
          sourceLocation: {
            startLine,
            endLine: this.previous().line,
          },
        };
      }

      // Handle inline comment after class name but before comma
      if (this.check(TokenType.Comment)) {
        this.advance(); // consume comment, we'll discard it as it's unusual
        this.skipWhitespaceAndNewlines();
      }

      if (!this.check(TokenType.Comma) && !this.check(TokenType.Semicolon)) {
        const token = this.peek();
        this.addError(`Expected ',' after class name "${className}"`, token.line, token.column);
        this.synchronize();
        return null;
      }
    }

    if (this.check(TokenType.Comma)) {
      this.advance(); // consume comma
    }

    // Parse fields
    const fields: IDFField[] = [];
    let terminated = false;

    while (!this.isAtEnd() && !terminated) {
      this.skipWhitespaceAndNewlines();

      if (this.isAtEnd()) break;

      // Check for object terminator
      // If we see semicolon here, it means we expected a field but got none (empty trailing field)
      // This happens with patterns like "Object,Value,;" where semicolon follows comma
      if (this.check(TokenType.Semicolon)) {
        this.advance();
        // Check for inline comment after semicolon
        this.skipWhitespace();
        let inlineComment: string | undefined;
        let units: string | undefined;
        if (this.check(TokenType.Comment)) {
          const commentToken = this.advance();
          inlineComment = this.extractCommentText(commentToken.value);
          units = parseUnitsFromComment(commentToken.value);
        }
        // Add empty field - we reached semicolon when expecting a field value
        // (if there were no fields expected, we wouldn't be in this loop)
        fields.push(createField('', inlineComment, units));
        terminated = true;
        break;
      }

      // Check for comma (empty field)
      if (this.check(TokenType.Comma)) {
        this.advance();
        // Check for inline comment after comma
        this.skipWhitespace();
        let inlineComment: string | undefined;
        let units: string | undefined;
        if (this.check(TokenType.Comment)) {
          const commentToken = this.advance();
          inlineComment = this.extractCommentText(commentToken.value);
          units = parseUnitsFromComment(commentToken.value);
        }
        fields.push(createField('', inlineComment, units));
        continue;
      }

      // Parse field value
      if (this.check(TokenType.FieldValue)) {
        const valueToken = this.advance();
        const fieldValue = valueToken.value;
        let inlineComment: string | undefined;
        let units: string | undefined;

        // Skip whitespace after value
        this.skipWhitespace();

        // Check for field terminator and inline comment
        if (this.check(TokenType.Comma)) {
          this.advance();
          // Check for inline comment after comma (IDF format: value, !- comment)
          this.skipWhitespace();
          if (this.check(TokenType.Comment)) {
            const commentToken = this.advance();
            inlineComment = this.extractCommentText(commentToken.value);
            units = parseUnitsFromComment(commentToken.value);
          }
          fields.push(createField(fieldValue, inlineComment, units));
        } else if (this.check(TokenType.Semicolon)) {
          this.advance();
          // Check for inline comment after semicolon
          this.skipWhitespace();
          if (this.check(TokenType.Comment)) {
            const commentToken = this.advance();
            inlineComment = this.extractCommentText(commentToken.value);
            units = parseUnitsFromComment(commentToken.value);
          }
          fields.push(createField(fieldValue, inlineComment, units));
          terminated = true;
        } else if (this.check(TokenType.Newline)) {
          // Newline without delimiter - might be error or just spacing
          fields.push(createField(fieldValue, inlineComment, units));
          this.skipWhitespaceAndNewlines();

          // Check what comes next
          if (this.check(TokenType.Comma)) {
            this.advance();
          } else if (this.check(TokenType.Semicolon)) {
            this.advance();
            terminated = true;
          } else if (
            this.check(TokenType.FieldValue) ||
            this.check(TokenType.Comment) ||
            this.isAtEnd()
          ) {
            // Likely missing delimiter, add warning and continue
            this.addWarning(
              `Missing delimiter after field value "${fieldValue}"`,
              valueToken.line,
              valueToken.column
            );
          }
        } else if (this.isAtEnd()) {
          fields.push(createField(fieldValue, inlineComment, units));
          this.addError(
            `Object "${className}" not terminated with semicolon`,
            valueToken.line,
            valueToken.column
          );
          terminated = true;
        }
      } else if (this.check(TokenType.Comment)) {
        // Standalone comment between fields - skip it
        this.advance();
      } else {
        // Unexpected token
        const token = this.peek();
        this.addError(
          `Unexpected token in object "${className}": ${token.value}`,
          token.line,
          token.column
        );
        this.synchronize();
        terminated = true;
      }
    }

    if (!terminated && !this.isAtEnd()) {
      const token = this.peek();
      this.addWarning(
        `Object "${className}" may not be properly terminated`,
        token.line,
        token.column
      );
    }

    return {
      id: generateId(),
      className,
      fields,
      leadingComments,
      sourceLocation: {
        startLine,
        endLine: this.previous().line,
      },
    };
  }

  /**
   * Skip over tokens until we find a stable point to resume parsing.
   */
  private synchronize(): void {
    while (!this.isAtEnd()) {
      // Stop at semicolon (end of object)
      if (this.check(TokenType.Semicolon)) {
        this.advance();
        return;
      }

      // If we see what looks like a new object, stop
      if (this.check(TokenType.FieldValue)) {
        // Look ahead to see if this is followed by comma (class name pattern)
        const saved = this.current;
        this.advance();
        this.skipWhitespace();
        if (this.check(TokenType.Comma)) {
          this.current = saved; // Reset to start of potential object
          return;
        }
        this.current = saved;
      }

      this.advance();
    }
  }

  private isAtEnd(): boolean {
    return this.peek().type === TokenType.EOF;
  }

  private peek(): Token {
    const token = this.tokens[this.current];
    if (!token) {
      throw new Error('Unexpected end of token stream');
    }
    return token;
  }

  private previous(): Token {
    const token = this.tokens[this.current - 1];
    if (!token) {
      throw new Error('No previous token available');
    }
    return token;
  }

  private advance(): Token {
    if (!this.isAtEnd()) {
      this.current++;
    }
    return this.previous();
  }

  private check(type: TokenType): boolean {
    if (this.isAtEnd()) return false;
    return this.peek().type === type;
  }

  private skipWhitespace(): void {
    while (this.check(TokenType.Whitespace)) {
      this.advance();
    }
  }

  private skipWhitespaceAndNewlines(): void {
    while (this.check(TokenType.Whitespace) || this.check(TokenType.Newline)) {
      this.advance();
    }
  }

  private addError(message: string, line: number, column?: number): void {
    const error: ParseError = { message, line, severity: 'error' };
    if (column !== undefined) error.column = column;
    this.errors.push(error);
  }

  private addWarning(message: string, line: number, column?: number): void {
    const warning: ParseError = { message, line, severity: 'warning' };
    if (column !== undefined) warning.column = column;
    this.errors.push(warning);
  }

  private extractCommentText(comment: string): string {
    if (comment.startsWith('!-')) {
      return comment.slice(2).trim();
    }
    if (comment.startsWith('!')) {
      return comment.slice(1).trim();
    }
    return comment.trim();
  }
}

/**
 * Convenience function to parse IDF content.
 */
export function parseIDF(input: string): ParseResult {
  const parser = new IDFParser();
  return parser.parse(input);
}
